'use client';

import { useContext, useState } from 'react';
import Image, { type ImageProps } from 'next/image';
import { QueryClientContext } from '@tanstack/react-query';

import { useIsPreview } from '@/contexts/preview';
import { useDebounce } from '@/hooks/use-debounce';
import {
  illustrationSrc,
  type IllustrationName,
  stockIllustrationSrc,
} from '@/lib/illustrations';
import useAppearanceSettings from '@/state/appearance-settings';

type IllustrationProps = Omit<ImageProps, 'src'> & { name: IllustrationName };

// The recoloured file is already a sized WebP, so it skips the optimizer
// rather than being re-encoded. Either way a failed load shows the original.
const IllustrationImage = ({
  name,
  alt,
  buttonColor,
  ...props
}: IllustrationProps & { buttonColor?: string }) => {
  const [failedSrc, setFailedSrc] = useState<string>();
  const src = illustrationSrc(name, buttonColor);
  const stock = stockIllustrationSrc(name);

  if (src === stock || src === failedSrc) {
    return <Image {...props} src={stock} alt={alt} />;
  }
  return (
    <Image
      {...props}
      src={src}
      alt={alt}
      unoptimized
      onError={() => setFailedSrc(src)}
    />
  );
};

// In the admin preview the colour follows a picker drag, which would otherwise
// ask for a new paint on every pointer move.
const BrandedIllustration = (props: IllustrationProps) => {
  const { data } = useAppearanceSettings();
  const buttonColor = useDebounce(
    data?.branding.button,
    useIsPreview() ? 400 : 0
  );
  return <IllustrationImage {...props} buttonColor={buttonColor} />;
};

// The root 404 and the global error page render outside the query provider,
// and on the stock palette, so they keep the green original.
export const Illustration = (props: IllustrationProps) => {
  const hasQueryClient = useContext(QueryClientContext) !== undefined;
  return hasQueryClient ? (
    <BrandedIllustration {...props} />
  ) : (
    <IllustrationImage {...props} />
  );
};
